import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Users, Home, ShoppingBag, CheckCircle, XCircle, Trash2, LogOut, ArrowLeft, Mail, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "admin", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("junction_admin_token") || "");
  const [stats, setStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [marketItems, setMarketItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, []);

  const headers = { Authorization: `Bearer ${token}` };

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setLoggedIn(true);
      fetchAll();
    } catch {
      localStorage.removeItem("junction_admin_token");
      setToken("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/admin/login`, loginForm, { withCredentials: true });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem("junction_admin_token", t);
      setLoggedIn(true);
      toast.success("Logged in as Admin");
      fetchAllWithToken(t);
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Invalid credentials");
    }
    setLoginLoading(false);
  };

  const fetchAll = () => fetchAllWithToken(token);

  const fetchAllWithToken = async (t) => {
    const h = { Authorization: `Bearer ${t}` };
    try {
      const [sRes, wRes, pRes, mRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: h }),
        axios.get(`${API}/admin/workers`, { headers: h }),
        axios.get(`${API}/admin/properties`, { headers: h }),
        axios.get(`${API}/admin/marketplace`, { headers: h }),
        axios.get(`${API}/admin/contacts`, { headers: h }),
      ]);
      setStats(sRes.data);
      setWorkers(wRes.data);
      setProperties(pRes.data);
      setMarketItems(mRes.data);
      setContacts(cRes.data);
    } catch {
      toast.error("Failed to fetch data");
    }
  };

  const toggleVerify = async (workerId) => {
    try {
      const res = await axios.patch(`${API}/admin/workers/${workerId}/verify`, {}, { headers });
      setWorkers(prev => prev.map(w => w.id === workerId ? { ...w, verified: res.data.verified } : w));
      toast.success(res.data.verified ? "Worker verified!" : "Verification removed");
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteWorker = async (workerId) => {
    try {
      await axios.delete(`${API}/admin/workers/${workerId}`, { headers });
      setWorkers(prev => prev.filter(w => w.id !== workerId));
      toast.success("Worker deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const deleteProperty = async (propId) => {
    try {
      await axios.delete(`${API}/admin/properties/${propId}`, { headers });
      setProperties(prev => prev.filter(p => p.id !== propId));
      toast.success("Property deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/admin/marketplace/${itemId}`, { headers });
      setMarketItems(prev => prev.filter(i => i.id !== itemId));
      toast.success("Item deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const logout = () => {
    localStorage.removeItem("junction_admin_token");
    setToken("");
    setLoggedIn(false);
    navigate("/");
  };

  // Login Screen
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <a href="/" className="flex items-center gap-2 justify-center mb-10">
            <div className="w-10 h-10 rounded-xl bg-indigo flex items-center justify-center">
              <span className="text-white font-heading font-bold text-xl">J</span>
            </div>
            <span className="font-heading font-semibold text-2xl text-indigo tracking-tight">Junction</span>
          </a>
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-50">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-2xl bg-indigo/10 flex items-center justify-center mx-auto mb-4">
                <Shield size={24} className="text-indigo" />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-indigo mb-2">Admin Panel</h1>
              <p className="text-sm text-gray-500">Login to manage Junction</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Username</label>
                <Input placeholder="admin" value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} className="rounded-xl bg-cream-alt border-none h-12" data-testid="admin-username-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Password</label>
                <Input type="password" placeholder="Enter password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="rounded-xl bg-cream-alt border-none h-12" data-testid="admin-password-input" />
              </div>
              <Button type="submit" disabled={loginLoading} className="w-full bg-indigo text-white hover:bg-indigo/90 rounded-full py-6 font-medium text-base" data-testid="admin-login-button">
                {loginLoading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </div>
          <p className="text-center text-xs text-gray-400 mt-6">
            <a href="/" className="text-terracotta hover:underline flex items-center justify-center gap-1"><ArrowLeft size={12} />Back to Junction</a>
          </p>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard-page">
      <div className="bg-indigo">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-marigold flex items-center justify-center">
                <span className="text-indigo font-heading font-bold text-sm">J</span>
              </div>
            </a>
            <span className="text-white/60 text-sm">Admin Dashboard</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-white/60 hover:text-white hover:bg-white/10 rounded-full" data-testid="admin-logout-button">
            <LogOut size={16} className="mr-1.5" />Logout
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 border border-gray-50" data-testid="admin-stat-workers">
              <div className="flex items-center gap-2 mb-2"><Users size={16} className="text-marigold" /><span className="text-xs text-gray-400">Workers</span></div>
              <p className="font-heading text-2xl font-semibold text-indigo">{stats.workers}</p>
              <p className="text-xs text-gray-400 mt-1">{stats.verified_workers} verified / {stats.unverified_workers} pending</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-50" data-testid="admin-stat-properties">
              <div className="flex items-center gap-2 mb-2"><Home size={16} className="text-sage" /><span className="text-xs text-gray-400">Properties</span></div>
              <p className="font-heading text-2xl font-semibold text-indigo">{stats.properties}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-50" data-testid="admin-stat-marketplace">
              <div className="flex items-center gap-2 mb-2"><ShoppingBag size={16} className="text-terracotta" /><span className="text-xs text-gray-400">Marketplace</span></div>
              <p className="font-heading text-2xl font-semibold text-indigo">{stats.marketplace_items}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-50" data-testid="admin-stat-contacts">
              <div className="flex items-center gap-2 mb-2"><Mail size={16} className="text-indigo" /><span className="text-xs text-gray-400">Inquiries</span></div>
              <p className="font-heading text-2xl font-semibold text-indigo">{stats.contacts}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-xl p-1 mb-6 border border-gray-100">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-indigo data-[state=active]:text-white" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="workers" className="rounded-lg data-[state=active]:bg-indigo data-[state=active]:text-white" data-testid="tab-workers">Workers ({workers.length})</TabsTrigger>
            <TabsTrigger value="properties" className="rounded-lg data-[state=active]:bg-indigo data-[state=active]:text-white" data-testid="tab-properties">Properties ({properties.length})</TabsTrigger>
            <TabsTrigger value="marketplace" className="rounded-lg data-[state=active]:bg-indigo data-[state=active]:text-white" data-testid="tab-marketplace">Marketplace ({marketItems.length})</TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-lg data-[state=active]:bg-indigo data-[state=active]:text-white" data-testid="tab-contacts">Inquiries ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="bg-white rounded-2xl p-6 border border-gray-50">
              <h2 className="font-heading text-xl font-medium text-indigo mb-4">Quick Overview</h2>
              <p className="text-gray-500 text-sm">Welcome to Junction Admin. Use the tabs above to manage workers, properties, marketplace items, and view contact inquiries.</p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-amber-800">Pending Verifications</p>
                  <p className="font-heading text-2xl font-semibold text-amber-900 mt-1">{workers.filter(w => !w.verified).length}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-green-800">Active Workers</p>
                  <p className="font-heading text-2xl font-semibold text-green-900 mt-1">{workers.filter(w => w.available).length}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="workers">
            <div className="space-y-3">
              {workers.map((w) => (
                <div key={w.id} className="bg-white rounded-2xl p-5 border border-gray-50 flex items-center justify-between" data-testid={`admin-worker-${w.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-marigold/10 flex items-center justify-center">
                      <span className="font-heading font-semibold text-marigold">{w.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-indigo">{w.name}</span>
                        {w.verified && <CheckCircle size={14} className="text-green-600" />}
                        <span className={`status-dot ${w.available ? 'available' : 'busy'}`}></span>
                      </div>
                      <p className="text-xs text-gray-400">{w.skill} - {w.area} - {w.contact}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant={w.verified ? "outline" : "default"} className={`rounded-full text-xs ${w.verified ? "border-gray-200" : "bg-sage text-white hover:bg-sage/90"}`} onClick={() => toggleVerify(w.id)} data-testid={`verify-worker-${w.id}`}>
                      {w.verified ? "Unverify" : "Verify"}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-xs border-red-200 text-red-500 hover:bg-red-50" onClick={() => deleteWorker(w.id)} data-testid={`delete-worker-${w.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="properties">
            <div className="space-y-3">
              {properties.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl p-5 border border-gray-50 flex items-center justify-between" data-testid={`admin-property-${p.id}`}>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-indigo">{p.title}</span>
                      <Badge className="rounded-full text-xs">{p.property_type === "rent" ? "Rent" : "Sale"}</Badge>
                      {p.is_nri && <Badge variant="outline" className="rounded-full text-xs border-marigold text-marigold">NRI</Badge>}
                    </div>
                    <p className="text-xs text-gray-400">{p.location} - {p.price} - {p.contact}</p>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full text-xs border-red-200 text-red-500 hover:bg-red-50" onClick={() => deleteProperty(p.id)} data-testid={`delete-property-${p.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="marketplace">
            <div className="space-y-3">
              {marketItems.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl p-5 border border-gray-50 flex items-center justify-between" data-testid={`admin-item-${item.id}`}>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-indigo">{item.title}</span>
                      <Badge className="rounded-full text-xs">{item.category}</Badge>
                    </div>
                    <p className="text-xs text-gray-400">INR {item.price} - {item.location} - {item.contact}</p>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full text-xs border-red-200 text-red-500 hover:bg-red-50" onClick={() => deleteItem(item.id)} data-testid={`delete-item-${item.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="space-y-3">
              {contacts.length === 0 && <p className="text-gray-400 text-center py-8">No inquiries yet.</p>}
              {contacts.map((c, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-gray-50" data-testid={`admin-contact-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-indigo">{c.name}</span>
                    <span className="text-xs text-gray-400">({c.email})</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1 font-medium">{c.subject}</p>
                  <p className="text-sm text-gray-600">{c.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
